/**
 * Stepper — N short bars + a "Step k of N" caption.
 *
 * Prototype's `.steps` block. Three states per bar:
 *   done   → ink fill
 *   active → sky fill
 *   future → line fill (the same color as borders, deliberately quiet)
 *
 * `current` is 1-based and clamped. Pass `done` to render the "Setup complete"
 * caption instead of the step counter — used on S05.
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Total number of bars. Default 4 — matches the four-step onboarding. */
  total?: number;
  /** 1-based current step. `total + 1` (or `done`) marks completion. */
  current: number;
  /** Override the caption. Default: "Step {current} of {total}" or "Setup complete". */
  label?: string;
  /** Mark every bar done; replace caption with "Setup complete". */
  done?: boolean;
  /** Optional per-bar tooltips; falls back to "Step n". */
  titles?: readonly string[];
}

export function Stepper({
  total = 4,
  current,
  label,
  done = false,
  titles,
  className,
  ...props
}: StepperProps) {
  const cur = done ? total + 1 : Math.max(1, Math.min(total, current));
  const caption = label ?? (done ? "Setup complete" : `Step ${cur} of ${total}`);

  return (
    <div
      className={cn("flex items-center gap-3", className)}
      aria-label="Onboarding progress"
      role="group"
      {...props}
    >
      <div className="flex items-center gap-1">
        {Array.from({ length: total }, (_, i) => {
          const idx = i + 1;
          const state = idx < cur ? "done" : idx === cur ? "active" : "future";
          return (
            <span
              key={idx}
              title={titles?.[i] ?? `Step ${idx}`}
              className={cn(
                "h-1 w-7 rounded-full transition-colors",
                state === "done" && "bg-ink",
                state === "active" && "bg-sky-600",
                state === "future" && "bg-line",
              )}
            />
          );
        })}
      </div>
      <span className="text-xs text-muted font-medium">{caption}</span>
    </div>
  );
}
